// How undo and redo rewrite and delete asset instances, keyframes and clips through v1 (untracked), then bring the
// renderer and the editor along. A delete is soft: it answers a deletion that restoreDeletion brings back whole,
// with everything that went with the row (a placement's keyframes, clips, effects, materials and clips' targets).

import type { components } from '@/generated/contracts/api-v1';
import type { RendererBridge } from '@/modules/rendererBridge';
import { v1 } from '@/infrastructure/api/v1/client';
import type { ScenePort } from './sceneWrites';
import { untracked } from './sceneWrites';
import type { SceneSync } from './scenePorts';
import { read, removeRow } from './v1Rows';
import { savingKeyframes } from '@/modules/keyframes/localEdits';
import { allowedKeyframeFields } from '@/modules/keyframes/anchorKeyframes';

type Schemas = components['schemas'];
type AssetInstanceDto = Schemas['AssetInstanceDto'];
type KeyframeDto = Schemas['KeyframeDto'];
type ClipInstanceDto = Schemas['ClipInstanceDto'];
type Bridge = Pick<RendererBridge, 'send'>;

/** How the editor's state follows a changed asset instance, keyframe or clip. */
export interface ContentEditor {
  showAsset(dto: AssetInstanceDto): Promise<void> | void;
  showKeyframe(dto: KeyframeDto, at?: number): Promise<void> | void;
  showClip(dto: ClipInstanceDto): Promise<void> | void;
}

/** The undo stack's ports for asset instances, keyframes and clips. `bridge` is gated on the undo's scene. */
export function contentPorts(bridge: Bridge, editor: ContentEditor, sync: SceneSync): { asset: ScenePort; keyframe: ScenePort; clip: ScenePort } {
  const asset: ScenePort = {
    get: id => read(v1.GET('/api/v1/asset-instances/{uniqueId}', { params: { path: { uniqueId: id } } })),
    async patch(id, fields) {
      const dto = await read(v1.PATCH('/api/v1/asset-instances/{uniqueId}', { params: { path: { uniqueId: id } }, body: fields, headers: untracked.headers }));
      bridge.send({ type: 'assetInstanceUpdated', assetInstance: dto });
      if ('isLocked' in fields) bridge.send({ type: 'assetInstanceLockChanged', assetInstance: dto });
      if ('isActive' in fields) bridge.send({ type: 'assetInstanceVisibilityChanged', assetInstance: dto });
      await editor.showAsset(dto);
      return dto;
    },
    remove: id => removeRow(sync, v1.DELETE('/api/v1/asset-instances/{uniqueId}', { params: { path: { uniqueId: id } }, headers: untracked.headers })),
  };

  const keyframe: ScenePort = {
    get: id => read(v1.GET('/api/v1/keyframes/{uniqueId}', { params: { path: { uniqueId: id } } })),
    async patch(id, fields) {
      const body = allowedKeyframeFields(id, fields);
      const { result: dto, tick } = await savingKeyframes([id], () => read(v1.PATCH('/api/v1/keyframes/{uniqueId}', { params: { path: { uniqueId: id } }, body, headers: untracked.headers })));
      bridge.send({ type: 'keyframeUpdated', keyframe: dto });
      await editor.showKeyframe(dto, tick);
      return dto;
    },
    remove: id => removeRow(sync, v1.DELETE('/api/v1/keyframes/{uniqueId}', { params: { path: { uniqueId: id } }, headers: untracked.headers })),
  };

  const clip: ScenePort = {
    get: id => read(v1.GET('/api/v1/clip-instances/{uniqueId}', { params: { path: { uniqueId: id } } })),
    async patch(id, fields) {
      const dto = await read(v1.PATCH('/api/v1/clip-instances/{uniqueId}', { params: { path: { uniqueId: id } }, body: fields, headers: untracked.headers }));
      bridge.send({ type: 'clipInstanceUpdated', clipInstance: dto });
      await editor.showClip(dto);
      return dto;
    },
    remove: id => removeRow(sync, v1.DELETE('/api/v1/clip-instances/{uniqueId}', { params: { path: { uniqueId: id } }, headers: untracked.headers })),
  };

  return { asset, keyframe, clip };
}
